import React, { useEffect, useMemo, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import {
  IFlight,
  IGroundEvent,
  IPairingPositionCounts,
  IPairing,
  IPlannedCreditMap,
  getDefaultPlannedCreditMap,
} from "@vmosolutions/oc-interfaces";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { DialogActions, Grid, useTheme } from "@mui/material";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { ModalTitle, ModalButton, ModalMessage, ModalErrorText } from "../DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { BulkPairingInfo } from "src/pages/FlightBuilder/BulkPairingInfo";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IShortCodeChip } from "../../ShortCodes/ShortCodeChip";
// Pairing Display components
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { DialogModalStyles } from "../DialogModalWrapper";

/**
 * Modal appears only when 1  or more flights is slected
 * creates a new pairing out of the selected flights
 */

interface IAssignFlightsToPairingModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const CreatePairingFromFlightsModal = (props: IAssignFlightsToPairingModalProps): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const [creatingPairing, setCreatingPairing] = useState<boolean>(false);
  const [newPairing, setNewPairing] = useState<IShortCodeChip>(null);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [pairingPosCounts, setPairingPosCounts] = useState<IPairingPositionCounts>(null);
  const [includeSplits, setIncludeSplits] = useState<boolean>(false);
  //later get the value from the DB to know if this value is defaulted to true or false
  const [isBiddable, setIsBiddable] = useState<boolean>(false);
  const [plannedCredit, setPlannedCredit] = useState<number>(undefined);
  const [plannedCreditMap, setPlannedCreditMap] = useState<IPlannedCreditMap>(undefined);

  const creditSettingValue = useSelector((state: IRootState) => state.credit);
  const creditSetting = useMemo(() => {
    return creditSettingValue.settings ?? [];
  }, [creditSettingValue]);

  const initialPlannedCreditMap = useMemo<IPlannedCreditMap>(() => {
    if (creditSetting && creditSetting.length > 0) {
      const defaultMap = getDefaultPlannedCreditMap(creditSetting);
      return defaultMap;
    }
    return undefined;
  }, [creditSetting]);

  useEffect(() => {
    if (creditSetting && creditSetting.length > 0) {
      const defaultMap = getDefaultPlannedCreditMap(creditSetting);
      setPlannedCreditMap(defaultMap);
    }
  }, [creditSetting]);

  const selectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const typeFlight: boolean = selectedFlights.length > 0 ? true : false;

  const handleCancel = (): void => {
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  //on unmount, if a new pairing was created, unselect the flights
  useEffect(() => {
    return () => {
      if (newPairing) {
        //deselect flights
        dispatch(setSelectedEvents([]));
      }
    };
  }, [dispatch, newPairing]);

  const handleCreatePairing = async (): Promise<void> => {
    setCreatingPairing(true);
    // Make an API call with the selected flights id and the positions
    const payload = {
      events: {
        flightIds: selectedFlights.map((flight) => flight._id),
      },
      positions: pairingPosCounts,
      includeSplits,
      isBiddable,
      plannedCredit,
      plannedCreditMap,
    };
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Creating New Pairing...",
      successMessage: "New Pairing Successfully Created",
      errorMessage: "Error: Unable to Create Pairing",
    };
    const response = await AmplifyAPIWrapper.Post<IPairing>("/pairings/events", payload, notifications);
    setCreatingPairing(false);
    if (response?._id && response?.shortId) {
      setNewPairing({ itemId: response?._id, code: response.shortId, type: "pairing" });
    } else {
      setErrorMessage(
        "We ran into a problem when attempting to create this pairing, please see administrator for assistance."
      );
    }
  };

  if (isOpen && selectedEvents.length > 0) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          {!!typeFlight && <FlightChipCollection flights={selectedFlights} />}

          {!newPairing && (
            <ModalTitle
              title={`Create Pairing from Selected Flight${selectedEvents.length > 1 ? "s" : ""}`}
            ></ModalTitle>
          )}
          {!newPairing && <ModalMessage message="Select one more available crew slots to continue"></ModalMessage>}
          {errorMessage && <ModalErrorText error={errorMessage}></ModalErrorText>}

          <DialogActions>
            <Grid container>
              {newPairing ? (
                <Grid container direction="column">
                  <ModalMessage message={"New Pairing Created"}></ModalMessage>
                  <ShortCodeChip {...newPairing} />
                </Grid>
              ) : (
                <Grid container alignItems="center" wrap="nowrap" style={{ width: "auto" }}>
                  <BulkPairingInfo
                    pairingPosCounts={pairingPosCounts}
                    setPairingPosCounts={setPairingPosCounts}
                    hideCheckbox={true}
                    includeSplits={includeSplits}
                    setIncludeSplits={setIncludeSplits}
                    isBiddable={isBiddable}
                    setIsBiddable={setIsBiddable}
                    plannedCredit={plannedCredit}
                    setPlannedCredit={setPlannedCredit}
                    hidePlannedCredit={creditSettingValue.hidePlannedCreditField}
                    initialPlannedCreditMap={initialPlannedCreditMap}
                    plannedCreditMap={plannedCreditMap}
                    setPlannedCreditMap={setPlannedCreditMap}
                  />
                </Grid>
              )}
              <Grid container alignItems="center" wrap="nowrap" style={{ width: "auto" }}>
                <ModalButton label={newPairing ? "Close" : "Cancel"} handler={handleCancel}></ModalButton>
                <ModalButton
                  label="Create Pairing"
                  handler={handleCreatePairing}
                  disable={
                    selectedEvents.length === 0 ||
                    !pairingPosCounts ||
                    creatingPairing ||
                    !!errorMessage ||
                    !!newPairing
                  }
                />
              </Grid>
            </Grid>
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
